import React, { useState, useEffect, useCallback } from 'react';
import { TextField, Button, Container, Typography, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, CircularProgress, Grid, Table, TableHead, TableBody, TableRow, TableCell, IconButton } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import QrScanner from 'react-qr-scanner';
import DeleteIcon from '@mui/icons-material/Delete';
import BusPhotoCapture from '../components/BusPhotoCapture';
import UnifiedManualInput from '../components/UnifiedManualInput';
import SmartManualInput from '../components/SmartManualInput';

function ValidatePassengers() {
  const { api, activeBus, clearActiveBus } = useAuth();
  const { busId } = useParams();
  const [barcode, setBarcode] = useState('');
  const [passengers, setPassengers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [isClosingBus, setIsClosingBus] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [busDetails, setBusDetails] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [isManualInput, setIsManualInput] = useState(true);
  const navigate = useNavigate();

  const [isManualInputOpen, setIsManualInputOpen] = useState(false);
  const [isSmartManualInputOpen, setIsSmartManualInputOpen] = useState(false);
  const [systemSettings, setSystemSettings] = useState([]);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [busPhoto, setBusPhoto] = useState(null);
  const [showTestUpload, setShowTestUpload] = useState(false);
  const [testUploadResult, setTestUploadResult] = useState(null);

  const [originMismatch, setOriginMismatch] = useState(null);
  const [flightRolloverNote, setFlightRolloverNote] = useState('');

  const handleManualSubmit = async (manualPassenger) => {
    setIsLoading(true);
    try {
      const response = await api.post('/passengers/manual-validate', {
        busId,
        ...manualPassenger
      });
      
      if (manualPassenger.paymentMethod === 'מזומן' || manualPassenger.paymentMethod === 'אשראי') {
        const transactionData = {
          touristId: response.data.tourist.MISPAR_HAZMANA,
          amount: manualPassenger.calculatedPrice || 0,
          originalAmount: manualPassenger.originalAmount,
          discountAmount: manualPassenger.discountAmount,
          discountReason: manualPassenger.discountReason,
          paymentMethod: getPaymentMethodEnum(manualPassenger.paymentMethod),
          notes: `תשלום עבור נסיעה ל${manualPassenger.originOrDestination} - תיקוף נוסע באוטובוס`,
          confirmationKey: manualPassenger.confirmationKey,
        };

        await api.post('/transactions', transactionData);
      }
      
      const newPassenger = {
        id: response.data.passenger.id,
        passengerName: `${response.data.tourist.PRATI || response.data.tourist.FIRST_NAME || ''} ${response.data.tourist.MISHPACHA || response.data.tourist.LAST_NAME || ''}`.trim(),
        barcode: response.data.tourist.MISPAR_HAZMANA,
        transitManagerName: response.data.passenger.transitManagerName
      };
      
      setPassengers(prevPassengers => [newPassenger, ...prevPassengers]);
      setIsManualInputOpen(false);
    } catch (error) {
      setDialogMessage(error.response?.data?.message || 'אירעה שגיאה בעת הזנת הנוסע');
      setOpenDialog(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getPaymentMethodEnum = (paymentMethod) => {
    switch (paymentMethod) {
      case 'מזומן': return 'cash';
      case 'פאייכלה': return 'paypal';
      case 'אשראי': return 'credit';
      default: return 'cash';
    }
  };

  const handleSmartValidationComplete = async (result) => {
    const { type, data } = result;
    
    if (type === 'direct_validation') {
      await handleDirectValidation(data);
    } else if (type === 'manual_validation') {
      await handleManualSubmit(data);
    }
  };

  const handleDirectValidation = async (touristData) => {
    setIsLoading(true);
    try {
      const response = await api.post('/passengers/validate', {
        busId,
        barcode: touristData.MISPAR_HAZMANA
      });
      
      const newPassenger = {
        id: response.data.id,
        passengerName: `${touristData.PRATI || touristData.FIRST_NAME || ''} ${touristData.MISHPACHA || touristData.LAST_NAME || ''}`.trim(),
        barcode: touristData.MISPAR_HAZMANA,
        transitManagerName: response.data.transitManagerName
      };
      
      setPassengers(prevPassengers => [newPassenger, ...prevPassengers]);
      setIsSmartManualInputOpen(false);
    } catch (error) {
      setDialogMessage(error.response?.data?.message || 'אירעה שגיאה בעת תיקוף הנוסע');
      setOpenDialog(true);
    } finally {
      setIsLoading(false);
    }
  };

  const [scannerSettings, setScannerSettings] = useState({
    delay: 300,
    resolution: 600,
    facingMode: "environment",
  });

  const fetchBusDetails = useCallback(async () => {
    try {
      const response = await api.get(`/buses/${busId}`);
      setBusDetails(response.data);
    } catch (error) {
      setDialogMessage('אירעה שגיאה בטעינת פרטי האוטובוס. אנא נסה שנית.');
      setOpenDialog(true);
      setBarcode('');
    }
  }, [api, busId]);

  const fetchPassengers = useCallback(async () => {
    try {
      const response = await api.get(`/passengers/bus/${busId}`);
      setPassengers(response.data);
    } catch (error) {
      setDialogMessage('אירעה שגיאה בטעינת רשימת הנוסעים. אנא נסה שנית.');
      setOpenDialog(true);
      setBarcode('');
    }
  }, [api, busId]);

  const fetchSystemSettings = useCallback(async () => {
    try {
      const response = await api.get('/settings');
      setSystemSettings(response.data);
    } catch (error) {
      console.error('נכשל בטעינת הגדרות המערכת', error);
    }
  }, [api]);

  const processBarcode = (rawBarcode) => {
    const parts = rawBarcode.split(',');
    if (parts.length >= 2) {
      return parts[1].trim(); 
    }
    return rawBarcode; 
  };

  const handleScan = (data) => {
    if (data) {
      const processedBarcode = processBarcode(data.text);
      setBarcode(processedBarcode);
      setIsScanning(false);
      setIsManualInput(false);
    }
  };

  const handleError = (err) => {
    setDialogMessage('אירעה שגיאה בסריקת הברקוד. אנא נסה שנית.');
    setOpenDialog(true);
    setBarcode('');
    setIsScanning(false);
  };

  const toggleCamera = () => {
    setScannerSettings(prev => ({
      ...prev,
      facingMode: prev.facingMode === "user" ? "environment" : "user"
    }));
  };

  const adjustScannerSettings = () => {
    setScannerSettings(prev => ({
      ...prev,
      delay: prev.delay === 300 ? 500 : 300,
      resolution: prev.resolution === 600 ? 800 : 600,
    }));
  };

  const handleManualInput = (e) => {
    setBarcode(e.target.value);
    setIsManualInput(true);
  };

  useEffect(() => {
    if (!busId) {
      setDialogMessage('מזהה האוטובוס חסר. אנא חזור לדף הקודם.');
      setOpenDialog(true);
      setBarcode('');
      setIsInitialLoading(false);
      return;
    }
    
    fetchBusDetails();
    fetchPassengers();
    fetchSystemSettings();
    setIsInitialLoading(false);
  }, [busId, activeBus, fetchBusDetails, fetchPassengers, fetchSystemSettings, navigate]);

  const handleValidate = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!barcode) return;
    
    const processedBarcode = processBarcode(barcode);
    
    setIsLoading(true);
    try {
      const pre = await api.post('/external-api/check-tourist-status', {
        identifier: processedBarcode,
        busId,
        busDetails
      });

      if (pre.data?.status === 'origin_mismatch') {
        setOriginMismatch({
          correctOrigin: pre.data?.data?.correctOrigin,
          tourist: pre.data?.data?.tourist,
          barcode: processedBarcode,
        });
        setBarcode('');
        return;
      }

      if (pre.data?.status !== 'can_validate') {
        throw { response: { data: { message: pre.data?.message || 'לא ניתן לתקף' } } };
      }

      const response = await api.post('/passengers/validate', { 
        busId, 
        barcode: processedBarcode
      });
      
      setPassengers(prevPassengers => [response.data, ...prevPassengers]);
      
      setBarcode('');
      setIsManualInput(true);
    } catch (error) {
      setDialogMessage(error.response?.data?.message || 'אירעה שגיאה בעת תיקוף הנוסע');
      setOpenDialog(true);
      setBarcode('');
    } finally {
      setIsLoading(false);
    }
  }, [api, busId, barcode, busDetails]);

  const handleRemovePassenger = async (passengerBarcode) => {
    setIsLoading(true);
    try {
      await api.post('/passengers/remove', { busId, barcode: passengerBarcode });
      setPassengers(prevPassengers => prevPassengers.filter(passenger => passenger.barcode !== passengerBarcode));
    } catch (error) {
      setDialogMessage('אירעה שגיאה בעת הסרת הנוסע');
      setOpenDialog(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (barcode && !isManualInput) {
      handleValidate();
    }
  }, [barcode, isManualInput, handleValidate]);

  const handleCloseBus = () => {
    if (!busDetails) return;
    
    if (passengers.length < busDetails.numberOfSeats) {
      setDialogMessage('לא ניתן לסגור אוטובוס שאינו מלא.');
      setOpenDialog(true);
    } else if (!busDetails.contactName || !busDetails.contactPhone) {
      setDialogMessage('לא ניתן לסגור אוטובוס ללא איש קשר וטלפון של איש קשר.');
      setOpenDialog(true);
    } else {
      setShowPhotoCapture(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsClosingBus(false);
  };

  const handlePhotoTaken = (imageBase64) => {
    setBusPhoto(imageBase64);
    setShowPhotoCapture(false);
    setDialogMessage('התמונה נשמרה בהצלחה. האם אתה בטוח שברצונך לסגור את האוטובוס?');
    setIsClosingBus(true);
    setOpenDialog(true);
  };

  const handlePhotoCaptureClose = () => {
    setShowPhotoCapture(false);
    setBusPhoto(null);
  };

  const handleCloseWithoutPhoto = () => {
    setShowPhotoCapture(false);
    setBusPhoto(null);
    setDialogMessage('האם אתה בטוח שברצונך לסגור את האוטובוס ללא תמונה?');
    setIsClosingBus(true);
    setOpenDialog(true);
  };

  const handleTestUpload = async (imageBase64) => {
    setIsLoading(true);
    try {
      const response = await api.post('/buses/test-upload', {
        imageBase64: imageBase64
      });
      
      setTestUploadResult({
        success: true,
        message: response.data.message,
        imageUrl: response.data.imageUrl
      });
      setShowTestUpload(false);
    } catch (error) {
      setTestUploadResult({
        success: false,
        message: error.response?.data?.message || 'שגיאה בהעלאה לבדיקה'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmCloseBus = async () => {
    setIsLoading(true);
    try {
      if (busPhoto) {
        await api.post(`/buses/${busId}/close-with-photo`, {
          imageBase64: busPhoto
        });
      } else {
        await api.post(`/buses/${busId}/close`);
      }
      
      await clearActiveBus();
      setOpenDialog(false);
      setIsClosingBus(false);
      navigate('/', { replace: true });

      setTimeout(() => {
        window.history.replaceState(null, '', '/');
      }, 0);
      
    } catch (error) {
      setDialogMessage('אירעה שגיאה בעת סגירת האוטובוס');
      setOpenDialog(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitialLoading) {
    return <CircularProgress />;
  }

  if (!busDetails) {
    return <CircularProgress />;
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography component="h1" variant="h5">
          תיקוף נוסעים
        </Typography>
        {busDetails && (
          <Box sx={{ mt: 2, mb: 2, width: '100%' }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body1" align="center">
                  <span style={{ fontWeight: 'bold' }}>סדרן:</span> {busDetails.transitManager}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body1" align="center">
                  <span style={{ fontWeight: 'bold' }}>אוטובוס:</span> {busDetails.busNumber}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" align="center">
                  <span style={{ fontWeight: 'bold' }}>יעד:</span> {busDetails.destination}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
        <Typography component="h2" variant="h6" sx={{ mt: 2 }}>
          מספר נוסעים שתוקפו: {passengers.length} / {busDetails?.numSeats || 'טוען...'}
        </Typography>
        <Box component="form" onSubmit={handleValidate} sx={{ mt: 1, width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="barcode"
            label="מספר הזמנה / ת.ז. / דרכון"
            name="barcode"
            autoFocus
            value={barcode}
            onChange={handleManualInput}
            disabled={isLoading}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'תקף נוסע'}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setIsScanning(true)}
            sx={{ mt: 1, mb: 2 }}
            disabled={isLoading}
          >
            סרוק ברקוד
          </Button>
        </Box>

        <Button
          fullWidth
          variant="outlined"
          color="primary"
          onClick={() => setIsSmartManualInputOpen(true)}
          sx={{ mt: 1, mb: 2 }}
          disabled={isLoading}
        >
          הזן הזמנה ידנית
        </Button>
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          onClick={handleCloseBus}
          sx={{ mt: 2 }}
          disabled={isLoading || passengers.length < (busDetails?.numberOfSeats || 0)}
        >
          סגירת אוטובוס
        </Button>
        
        <Typography component="h2" variant="h6" sx={{ mt: 4 }}>
          נוסעים שתוקפו:
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>שם</TableCell>
              <TableCell>הזמנה</TableCell>
              <TableCell>סדרן</TableCell>
              <TableCell>הסר</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {passengers.map((passenger) => (
              <TableRow key={passenger.id}>
                <TableCell>{passenger.passengerName}</TableCell>
                <TableCell>{passenger.barcode}</TableCell>
                <TableCell>{passenger.transitManagerName}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleRemovePassenger(passenger.barcode)} disabled={isLoading}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>הודעה</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {isClosingBus ? (
            <>
              <Button onClick={handleCloseDialog} disabled={isLoading}>ביטול</Button>
              <Button onClick={handleConfirmCloseBus} autoFocus disabled={isLoading}>
                {isLoading ? <CircularProgress size={24} /> : 'אישור'}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseDialog} autoFocus>
              אישור
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={isScanning} onClose={() => setIsScanning(false)}>
        <DialogTitle>סריקת ברקוד</DialogTitle>
        <DialogContent>
          <QrScanner
            delay={scannerSettings.delay}
            onError={handleError}
            onScan={handleScan}
            style={{ width: '100%' }}
            constraints={{
              video: { 
                facingMode: scannerSettings.facingMode,
                width: { min: scannerSettings.resolution },
                height: { min: scannerSettings.resolution },
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={toggleCamera}>החלף מצלמה</Button>
          <Button onClick={adjustScannerSettings}>שנה הגדרות סריקה</Button>
          <Button onClick={() => setIsScanning(false)}>ביטול</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isManualInputOpen}
        onClose={() => setIsManualInputOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          <UnifiedManualInput 
            context="bus-validation"
            busId={busId}
            busDetails={busDetails}
            systemSettings={systemSettings}
            onSubmit={handleManualSubmit}
          />
        </DialogContent>
      </Dialog>

      <SmartManualInput
        open={isSmartManualInputOpen}
        onClose={() => setIsSmartManualInputOpen(false)}
        busId={busId}
        busDetails={busDetails}
        systemSettings={systemSettings}
        onValidationComplete={handleSmartValidationComplete}
      />

      <BusPhotoCapture
        open={showPhotoCapture}
        onClose={handlePhotoCaptureClose}
        onPhotoTaken={handlePhotoTaken}
        busNumber={busDetails?.busNumber}
        allowCloseWithoutPhoto={true}
        onCloseWithoutPhoto={handleCloseWithoutPhoto}
      />

      <BusPhotoCapture
        open={showTestUpload}
        onClose={() => setShowTestUpload(false)}
        onPhotoTaken={handleTestUpload}
        busNumber="בדיקה"
      />

      <Dialog open={!!testUploadResult} onClose={() => setTestUploadResult(null)}>
        <DialogTitle>
          {testUploadResult?.success ? '✅ בדיקה הצליחה!' : '❌ בדיקה נכשלה'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {testUploadResult?.message}
          </DialogContentText>
          {testUploadResult?.success && testUploadResult?.imageUrl && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">קישור לתמונה:</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all', color: 'primary.main' }}>
                {testUploadResult.imageUrl}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => window.open(testUploadResult.imageUrl, '_blank')}
                sx={{ mt: 1 }}
              >
                פתח תמונה
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestUploadResult(null)}>סגור</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!originMismatch} onClose={() => {
        setOriginMismatch(null);
        setFlightRolloverNote('');
      }}>
        <DialogTitle>המוצא לא תואם</DialogTitle>
        <DialogContent>
          <DialogContentText>
            המוצא הוא {originMismatch?.correctOrigin || 'לא ידוע'}. כדי לתקף בכל זאת, כתוב גלגול טיסה:
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={flightRolloverNote}
            onChange={(e) => setFlightRolloverNote(e.target.value)}
            sx={{ mt: 2 }}
            placeholder='כתוב בדיוק "גלגול טיסה"'
            autoFocus
            error={flightRolloverNote && flightRolloverNote.trim() !== 'גלגול טיסה'}
            helperText={
              flightRolloverNote && flightRolloverNote.trim() !== 'גלגול טיסה' 
                ? 'יש לכתוב בדיוק "גלגול טיסה"' 
                : 'כתוב בדיוק את המילים "גלגול טיסה"'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOriginMismatch(null);
              setFlightRolloverNote('');
            }} 
            disabled={isLoading}
          >
            ביטול
          </Button>
          <Button
            variant="contained"
            disabled={
              !flightRolloverNote || 
              flightRolloverNote.trim() !== 'גלגול טיסה' || 
              isLoading
            }
            onClick={async () => {
              if (flightRolloverNote.trim() !== 'גלגול טיסה') {
                setDialogMessage('יש לכתוב בדיוק "גלגול טיסה"');
                setOpenDialog(true);
                return;
              }
              
              try {
                setIsLoading(true);
                
                const response = await api.post('/passengers/validate', {
                  busId,
                  barcode: originMismatch?.barcode,
                  flightRolloverNote: flightRolloverNote.trim()
                });
                
                setPassengers(prevPassengers => [response.data, ...prevPassengers]);
                
                setFlightRolloverNote('');
                setOriginMismatch(null);
                setBarcode('');
              } catch (err) {
                setDialogMessage(err.response?.data?.message || 'שגיאה בתיקוף');
                setOpenDialog(true);
              } finally {
                setIsLoading(false);
              }
            }}
          >
            {isLoading ? <CircularProgress size={20} /> : 'אישור'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ValidatePassengers;